import React, { useState, useEffect } from 'react';
import { addTermin, getOrganizationLeads } from '../../api';

function AddTerminDialog({
  user,
  selectedDate,
  selectedTimeSlot,
  selectedTeam,
  teams,
  preSelectedCustomer,
  onSave,
  onCancel,
  isEditMode = false,
  existingTermin = null,
  dialogTitle = "Neuen Termin erstellen"
}) {

  const [formData, setFormData] = useState({
    datum: '',
    uhrzeit: '',
    dauer: '120', // Standard: 2 Stunden
    kundeId: '',
    teamId: selectedTeam || '',
    notizen: '',
    art: '' // Termin-Art leer - muss ausgewählt werden
  });

  const [kunden, setKunden] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (selectedDate) {
      const dateString = selectedDate.toISOString().split('T')[0];
      setFormData(prev => ({
        ...prev,
        datum: dateString,
        uhrzeit: selectedTimeSlot || ''
      }));
    }
  }, [selectedDate, selectedTimeSlot]);

  // PreSelectedCustomer setzen
  useEffect(() => {
    if (preSelectedCustomer) {
      setFormData(prev => ({
        ...prev,
        kundeId: preSelectedCustomer.id
      }));
    }
  }, [preSelectedCustomer]);

  useEffect(() => {
    if (!preSelectedCustomer) {
      loadKunden();
    }
  }, [formData.teamId, preSelectedCustomer]);

  // Edit-Modus: Felder mit bestehenden Werten vorbelegen
  useEffect(() => {
    if (isEditMode && existingTermin) {
      // Uhrzeit ggf. auf HH:mm kürzen
      let uhrzeit = existingTermin.startzeit || existingTermin.uhrzeit || '';
      if (uhrzeit.length === 8 && uhrzeit.match(/^\d{2}:\d{2}:\d{2}$/)) {
        uhrzeit = uhrzeit.substring(0, 5);
      }
      setFormData(prev => ({
        ...prev,
        datum: existingTermin.datum || '',
        uhrzeit: uhrzeit,
        dauer: existingTermin.dauer ? existingTermin.dauer.toString() : '60'
      }));
    }
  }, [isEditMode, existingTermin]);

  const loadKunden = async () => {
    try {
      const response = await getOrganizationLeads(user.id, user.organization_id);
      if (response.success) {
        let filteredKunden = response.leads || [];

        // Nach Team filtern, wenn ein Team ausgewählt ist
        if (formData.teamId) {
          filteredKunden = filteredKunden.filter(kunde =>
            kunde.team_id && kunde.team_id.toString() === formData.teamId.toString()
          );
        }

        setKunden(filteredKunden);
      }
    } catch (err) {
      setError('Fehler beim Laden der Kunden: ' + err.message);
    }
  };

  const getTeamNameById = (teamId) => {

    if (!teamId || !teams || teams.length === 0) {
      return null;
    }

    const team = teams.find(t => t.id.toString() === teamId.toString());

    return team ? team.name : null;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Wenn eine Termin-Art ausgewählt wird, Dauer automatisch setzen
    if (name === 'art') {
      let defaultDuration = '120'; // Standard: 2 Stunden

      switch (value) {
        case 'Beratungstermin':
          defaultDuration = '120'; // 2 Stunden
          break;
        case 'Telefonisches Erstgespräch':
          defaultDuration = '15'; // 15 Minuten
          break;
        case 'Nachtermin':
        case 'Beratung':
        case 'Präsentation':
        case 'Vertragsabschluss':
          defaultDuration = '60'; // 1 Stunde
          break;
        default:
          defaultDuration = '60';
      }

      setFormData(prev => ({
        ...prev,
        [name]: value,
        dauer: defaultDuration
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  // Zeitslots für Dropdown generieren (08:00 - 22:00)
  const generateTimeSlots = () => {
    const slots = [];
    for (let hour = 8; hour <= 21; hour++) {
      slots.push(`${hour.toString().padStart(2, '0')}:00`);
      slots.push(`${hour.toString().padStart(2, '0')}:15`);
      slots.push(`${hour.toString().padStart(2, '0')}:30`);
      if (hour < 21) {
        slots.push(`${hour.toString().padStart(2, '0')}:45`);
      }
    }
    // 22:00 hinzufügen
    slots.push('22:00');
    return slots;
  };

  // Terminlängen in 15-Minuten-Schritten generieren (15 Minuten bis 14 Stunden)
  const generateDurationOptions = () => {
    const durations = [];
    for (let minutes = 15; minutes <= 840; minutes += 15) {
      const hours = Math.floor(minutes / 60);
      const remainingMinutes = minutes % 60;

      let label = '';
      if (hours > 0) {
        label += `${hours}h`;
        if (remainingMinutes > 0) {
          label += ` ${remainingMinutes}min`;
        }
      } else {
        label = `${remainingMinutes} min`;
      }

      durations.push({
        value: minutes.toString(),
        label: label
      });
    }
    return durations;
  };

  // Endzeit berechnen für Anzeige
  const calculateEndTime = () => {
    if (!formData.uhrzeit || !formData.dauer) return '';

    const [hours, minutes] = formData.uhrzeit.split(':').map(Number);
    const durationMinutes = parseInt(formData.dauer);

    const startDate = new Date();
    startDate.setHours(hours, minutes, 0, 0);

    const endDate = new Date(startDate.getTime() + durationMinutes * 60000);

    return endDate.toLocaleTimeString('de-DE', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Validierung: Termin-Art muss ausgewählt sein
      if (!formData.art) {
        setError('Bitte wählen Sie eine Termin-Art aus');
        setLoading(false);
        return;
      }

      if (isEditMode) {
        if (!formData.datum || !formData.uhrzeit || !formData.dauer) {
          setError('Bitte alle Pflichtfelder ausfüllen');
          setLoading(false);
          return;
        }
      } else {
        if (!formData.datum || !formData.uhrzeit) {
          setError('Bitte alle Pflichtfelder ausfüllen');
          setLoading(false);
          return;
        }
      }

      console.log("formData : ", formData)

      // WICHTIG: Die Daten müssen korrekt an onSave übergeben werden
      const terminData = {
        datum: formData.datum,
        uhrzeit: formData.uhrzeit,
        dauer: formData.dauer, // Hinzugefügt: Dauer
        kundeId: formData.kundeId, // Hinzugefügt: Kunde ID
        notizen: formData.notizen, // Hinzugefügt: Notizen
        team_id: formData.teamId,
        team_name: getTeamNameById(formData.teamId),
        art: formData.art
      };

      if (onSave && typeof onSave === 'function') {
        await onSave(terminData); // Daten als Parameter übergeben
      } else {
        console.error('❌ onSave ist keine Funktion:', onSave);
        setError('Speicherfunktion nicht verfügbar');
      }

    } catch (error) {
      console.error('❌ Fehler in AddTerminDialog handleSubmit:', error);
      setError('Fehler beim Speichern: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const durationOptions = generateDurationOptions();

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h2>{dialogTitle}</h2>
          <button className="close-button" onClick={onCancel}>×</button>
        </div>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit}>
          {isEditMode ? (
            <>
              <div className="form-row">
                <div className="form-field">
                  <label>Neues Datum:</label>
                  <input
                    type="date"
                    name="datum"
                    value={formData.datum}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-field">
                  <label>Neue Uhrzeit:</label>
                  <select
                    name="uhrzeit"
                    value={formData.uhrzeit}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Uhrzeit auswählen</option>
                    {generateTimeSlots().map(time => (
                      <option key={time} value={time}>{time}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-field">
                  <label>Dauer (in Minuten):</label>
                  <select
                    name="dauer"
                    value={formData.dauer}
                    onChange={handleChange}
                    required
                  >
                    {durationOptions.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-field">
                  <label>Termin-Art:</label>
                  <select
                    name="art"
                    value={formData.art}
                    onChange={handleChange}
                    required
                  >
                    <option value="">-- Termin-Art auswählen --</option>
                    <option value="Beratungstermin">Beratungstermin</option>
                    <option value="Telefonisches Erstgespräch">Telefonisches Erstgespräch</option>
                  </select>
                </div>
              </div>
            </>
          ) : (
            <>
              {/* Team-Auswahl nur anzeigen, wenn kein preSelectedCustomer */}
              {!preSelectedCustomer && (
                <div className="form-row">
                  <div className="form-field">
                    <label>Projekt</label>
                    <select
                      name="teamId"
                      value={formData.teamId}
                      onChange={handleChange}
                    >
                      <option value="">Alle Teams</option>
                      {teams.map(team => (
                        <option key={team.id} value={team.id}>
                          {team.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              <div className="form-row">
                <div className="form-field">
                  <label>Datum *</label>
                  <input
                    type="date"
                    name="datum"
                    value={formData.datum}
                    onChange={handleChange}
                    required
                    min={new Date().toISOString().split('T')[0]}
                  />
                </div>
                <div className="form-field">
                  <label>Uhrzeit *</label>
                  <select
                    name="uhrzeit"
                    value={formData.uhrzeit}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Uhrzeit auswählen</option>
                    {generateTimeSlots().map(time => (
                      <option key={time} value={time}>
                        {time}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-field">
                  <label>Dauer *</label>
                  <select
                    name="dauer"
                    value={formData.dauer}
                    onChange={handleChange}
                    required
                  >
                    {durationOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-field">
                  <label>Endzeit (automatisch)</label>
                  <div className="calculated-time">
                    {calculateEndTime() || 'Startzeit wählen'}
                  </div>
                </div>
              </div>

              <div className="form-row">
                <div className="form-field">
                  <label>Termin-Art *</label>
                  <select
                    name="art"
                    value={formData.art}
                    onChange={handleChange}
                    required
                  >
                    <option value="">-- Termin-Art auswählen --</option>
                    <option value="Beratungstermin">Beratungstermin</option>
                    <option value="Telefonisches Erstgespräch">Telefonisches Erstgespräch</option>
                  </select>
                </div>
              </div>

              {/* Kunde - entweder Dropdown oder fester Wert */}
              {!preSelectedCustomer ? (
                <div className="form-row">
                  <div className="form-field">
                    <label>Kunde *</label>
                    <select
                      name="kundeId"
                      value={formData.kundeId}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Kunde auswählen</option>
                      {kunden.map(kunde => (
                        <option key={kunde.id} value={kunde.id}>
                          {kunde.name} {kunde.email ? `(${kunde.email})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              ) : (
                <div className="form-row">
                  <div className="form-field">
                    <label>Kunde</label>
                    <div className="selected-customer">
                      {preSelectedCustomer.name} {preSelectedCustomer.email ? `(${preSelectedCustomer.email})` : ''}
                    </div>
                  </div>
                </div>
              )}

              <div className="form-row">
                <div className="form-field">
                  <label>Notizen</label>
                  <textarea
                    name="notizen"
                    value={formData.notizen}
                    onChange={handleChange}
                    rows={3}
                    placeholder="Zusätzliche Informationen zum Termin..."
                  ></textarea>
                </div>
              </div>
            </>
          )}

          <div className="modal-actions">
            <button type="button" onClick={onCancel} className="cancel-button">
              Abbrechen
            </button>
            <button type="submit" disabled={loading}>
              {loading ? (isEditMode ? 'Wird aktualisiert...' : 'Wird erstellt...') : (isEditMode ? 'Termin aktualisieren' : 'Termin erstellen')}
            </button>
          </div>
        </form>
      </div>

      <style jsx>{`
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1000;
        }

        .modal-content {
          background: white;
          border-radius: 8px;
          padding: 24px;
          max-width: 500px;
          width: 90%;
          max-height: 90vh;
          overflow-y: auto;
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
        }

        .modal-header h2 {
          margin: 0;
          color: #2d3748;
          font-size: 1.25rem;
        }

        .close-button {
          background: none;
          border: none;
          font-size: 1.5rem;
          cursor: pointer;
        }

        .form-row {
          display: flex;
          gap: 15px;
          margin-bottom: 15px;
        }

        .form-field {
          flex: 1;
          display: flex;
          flex-direction: column;
        }

        .form-field label {
          font-weight: 500;
          color: #4a5568;
          margin-bottom: 5px;
          font-size: 0.9rem;
        }

        .form-field input,
        .form-field select,
        .form-field textarea {
          padding: 8px 12px;
          border: 1px solid #e2e8f0;
          border-radius: 6px;
          font-size: 0.9rem;
          transition: border-color 0.2s;
        }

        .form-field input:focus,
        .form-field select:focus,
        .form-field textarea:focus {
          outline: none;
          border-color: #4a6de5;
          box-shadow: 0 0 0 3px rgba(74, 109, 229, 0.1);
        }

        .calculated-time {
          padding: 8px 12px;
          border: 1px solid #e2e8f0;
          border-radius: 6px;
          background-color: #f8fafc;
          color: #4a5568;
          font-size: 0.9rem;
          min-height: 38px;
          display: flex;
          align-items: center;
          font-weight: 500;
        }

        .selected-customer {
          padding: 8px 12px;
          border: 1px solid #e2e8f0;
          border-radius: 6px;
          background-color: #f8fafc;
          color: #2d3748;
          font-size: 0.9rem;
          min-height: 38px;
          display: flex;
          align-items: center;
          font-weight: 500;
        }

        .modal-actions {
          display: flex;
          gap: 10px;
          justify-content: flex-end;
          margin-top: 20px;
        }

        .modal-actions button {
          padding: 10px 20px;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          font-weight: 500;
          transition: all 0.2s;
        }

        .cancel-button {
          background: #f3f4f6;
          color: #4b5563;
        }

        .cancel-button:hover {
          background: #e5e7eb;
        }

        .modal-actions button[type="submit"] {
          background: #4a6de5;
          color: white;
        }

        .modal-actions button[type="submit"]:hover:not(:disabled) {
          background: #3c5ce0;
        }

        .modal-actions button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .error-message {
          color: #e53e3e;
          background: #fed7d7;
          padding: 8px 12px;
          border-radius: 6px;
          margin-bottom: 15px;
          font-size: 0.9rem;
        }

        @media (max-width: 768px) {
          .form-row {
            flex-direction: column;
            gap: 10px;
          }

          .modal-content {
            padding: 20px;
            max-width: 95%;
          }
        }
      `}</style>
    </div>
  );
}

export default AddTerminDialog;